import {
  ArrowRight,
  Code2,
  Gem,
  Layers,
  ListChecks,
  Pencil,
  Plus,
} from "lucide-react";
import { Link, useHistory } from "react-router-dom";

import { PageTitle } from "components/page-title";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import {
  PermissionsWrapper,
  PermissionTooltipButton,
} from "components/permissions/permissions";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card } from "components/ui/card";
import * as Routes from "routes";

import { AppAccessIllustration } from "./app-access-illustration";

const steps = [
  {
    icon: Pencil,
    title: "Name your app",
    description:
      "Give your integration a name so it’s easy to find and manage.",
  },
  {
    icon: ListChecks,
    title: "Choose your APIs",
    description:
      "Select the Geoscape APIs your app needs, from verification to spatial data.",
  },
  {
    icon: Code2,
    title: "Start integrating",
    description:
      "Use your app’s API key and our documentation to make your first request.",
  },
];

export const FirstAppEmptyState = () => {
  const history = useHistory();

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageTitle
        icon={Layers}
        title="My Apps"
        description="Create and manage your Geoscape integrations and apps."
      />
      <Card className="gap-0 overflow-hidden p-0">
        <div className="grid lg:grid-cols-2">
          <div className="flex flex-col justify-center p-6 sm:p-10">
            <Badge variant="secondary" className="mb-5 gap-1.5 self-start">
              <Code2 aria-hidden="true" />
              Build with Geoscape
            </Badge>
            <h2 className="max-w-md text-3xl font-semibold tracking-tight sm:text-4xl">
              Your first app starts here.
            </h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground">
              Connect Geoscape APIs to your products and workflows. Create an
              app to choose your APIs and get an API key.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <PermissionTooltipButton
                permissionKeys={[PermissionsKeys.appsCreate]}
                size="lg"
                onClick={() => history.push(Routes.developerApiKeysCreate)}
              >
                <Plus aria-hidden="true" />
                Create your first app
              </PermissionTooltipButton>
              <Button asChild variant="link" size="lg">
                <Link to={Routes.docsPage}>
                  Explore API docs
                  <ArrowRight aria-hidden="true" />
                </Link>
              </Button>
            </div>
            <PermissionsWrapper
              keys={[PermissionsKeys.premiumDataRead]}
              messages={{
                [PermissionsReasons.ROLE_DENY]: <></>,
                [PermissionsReasons.UPGRADE_REQUIRED]: (
                  <p className="mt-5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs leading-relaxed text-muted-foreground">
                    <Gem className="size-3.5 shrink-0" aria-hidden="true" />
                    Some APIs require a paid plan.
                    <Link
                      className="text-primary underline-offset-4 hover:underline"
                      to={Routes.orgManageSubscription}
                    >
                      View plans
                    </Link>
                  </p>
                ),
              }}
              showLoadingState={false}
            >
              <></>
            </PermissionsWrapper>
          </div>
          <AppAccessIllustration />
        </div>
        <div className="grid gap-6 border-t bg-muted/20 p-6 sm:p-8 md:grid-cols-3">
          {steps.map(({ icon: Icon, title, description }, index) => (
            <div key={title}>
              <div className="mb-3 flex items-center gap-2 text-primary">
                <span className="grid size-6 place-items-center rounded-full bg-primary/10 text-xs font-semibold">
                  {index + 1}
                </span>
                <Icon className="size-4" aria-hidden="true" />
              </div>
              <h3 className="text-sm font-semibold">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {description}
              </p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
